<template>
  <vue-loading v-if="loading" :active.sync="loading" :is-full-page="true" loader="dots" color="#70A1F0"></vue-loading>
  <div v-else id="shelvery-status">

    <!-- always rendered: selecting a period with no data must not hide the way back to Latest -->
    <nav class="navbar fixed-top-sub navbar-light bg-light">
      <div class="navbar-brand">Shelvery Run Status</div>
      <ul class="nav nav-pages mr-auto">
        <li class="nav-item" v-for="tab in visibleTabs" :key="tab.id">
          <a class="nav-link" :class="{'active': activeTab.id === tab.id}" type="button" @click="setTab(tab)">
            {{tab.name}}
          </a>
        </li>
      </ul>

      <div class="nav-item btn-group dropdown">
        <button type="button" class="btn-block btn dropdown-toggle btn-secondary text-right" style="margin-right: 10px;" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
          {{periodShort}}
        </button>
        <div class="dropdown-menu dropdown-menu-right scrollable-menu">
          <a class="dropdown-item" :class="{'active-period': period === null}" @click="setPeriod(null, null)">Latest</a>
          <h6 class="dropdown-header">Days</h6>
          <a class="dropdown-item" v-for="day in dayOptions" :key="day"
             :class="{'active-period': period === 'day' && date === day}"
             @click="setPeriod('day', day)">{{formatDay(day)}}</a>
          <h6 class="dropdown-header">Months</h6>
          <a class="dropdown-item" v-for="month in monthOptions" :key="month"
             :class="{'active-period': period === 'month' && date === month}"
             @click="setPeriod('month', month)">{{formatMonth(month)}}</a>
        </div>
      </div>

      <div class="nav-item btn-group dropdown" v-if="accounts.length > 1">
        <button type="button" class="btn-block btn dropdown-toggle btn-secondary text-right" style="margin-right: 10px;" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
          {{activeAccount}}
        </button>
        <div class="dropdown-menu dropdown-menu-right scrollable-menu">
          <h6 class="dropdown-header">Accounts</h6>
          <div v-for="account in accounts" :key="account">
            <a class="dropdown-item" @click="setAccount(account)">{{account}}</a>
          </div>
        </div>
      </div>

      <button type="button" class="btn btn-primary text-right" v-if="allReports.length > 0" @click="downloadCSV">Download CSV</button>
    </nav>

    <div class="row">
      <div class="column">

        <template v-if="allReports.length > 0">

          <transition name="fade" appear>
            <div class="card">
              <div class="card-heading"><h4>{{healthHeading}}</h4></div>
              <div class="card-body">
                <div class="kpi-row">
                  <div class="kpi" v-for="tile in kpis" :key="tile.label">
                    <span class="kpi-value" :class="tile.tone">{{tile.value}}</span>
                    <span class="kpi-label">{{tile.label}}</span>
                  </div>
                </div>
                <p class="kpi-note">{{kpiNote}}</p>
              </div>
            </div>
          </transition>

          <transition name="fade" appear>
            <div class="card issue-card" v-if="failingRuns.length > 0">
              <div class="card-heading"><h4>Needs attention</h4></div>
              <div class="card-body">
                <ul class="attention-list">
                  <li class="attention-row" v-for="run in failingRuns" :key="runKey(run)" @click="jumpTo(run)">
                    <span>
                      <span class="attention-account">{{run.account_name}}</span>
                      <span class="attention-detail">{{run.backup_type}} &middot; {{run.action}}</span>
                    </span>
                    <span class="status-chip" :class="statusClass(run.status)">{{run.status}}</span>
                  </li>
                </ul>
              </div>
            </div>
          </transition>

          <transition name="fade" appear>
            <div class="card">
              <div class="card-body">
                <vue-good-table
                :columns="tableColumns"
                :rows="rows"
                :search-options="{
                  enabled: true,
                  skipDiacritics: true,
                  placeholder: 'filter runs',
                }"
                :pagination-options="{
                  enabled: true,
                  perPage: 25,
                }"
                :sort-options="{
                  enabled: true,
                  initialSortBy: {field: 'status', type: 'desc'}
                }"
                :row-style-class="rowStyleFn"
                @on-row-click="onRowClick"
                styleClass="vgt-table condensed">
                <div slot="table-actions">
                  <button type="button" class="btn btn-secondary btn-margin btn-sm" v-if="hiddenInactive > 0 || showInactive"
                          v-on:click="showInactive = !showInactive">
                    {{showInactive ? 'Hide inactive' : hiddenInactive + ' inactive hidden'}}
                  </button>
                  <button type="button" class="btn btn-danger btn-margin btn-sm" v-on:click="showOnlyFailures = !showOnlyFailures">
                    {{showOnlyFailures ? 'Show all runs' : 'Show failures only'}}
                  </button>
                </div>
                <template slot="table-row" slot-scope="props">
                  <span v-if="props.column.field === 'status'"
                        class="status-chip" :class="statusClass(props.row.status)">{{props.row.status}}</span>
                  <span v-else>{{props.formattedRow[props.column.field]}}</span>
                </template>
                <div slot="emptystate" class="text-center">
                  nothing to show for {{activeTab.name.toLowerCase()}} in {{activeAccount}}
                </div>
                </vue-good-table>
              </div>
            </div>
          </transition>

          <transition name="fade" appear>
            <div class="card issue-card" v-if="selected != null">
              <div class="card-heading">
                <h4>{{selected.backup_type}} {{selected.action}}</h4>
              </div>
              <div class="card-body run-detail">
                <dl v-if="isMonthView">
                  <dt>Month</dt>
                  <dd>{{formatMonth(selected.month)}}</dd>
                  <dt>Account</dt>
                  <dd>{{selected.account_name}} <span class="muted">({{selected.account_id}})</span></dd>
                  <dt>Scope</dt>
                  <dd>{{selected.backup_type}} &middot; {{selected.action}} &middot; {{selected.region}}</dd>
                  <dt>Days run</dt>
                  <dd>{{selected.days_run}} <span class="muted">({{selected.runs}} runs)</span></dd>
                  <dt>Ran between</dt>
                  <dd>{{formatDay(selected.first_run)}} &ndash; {{formatDay(selected.last_run)}}</dd>
                  <dt>Status</dt>
                  <dd><span class="status-chip" :class="statusClass(selected.status)">{{selected.status}}</span></dd>
                </dl>

                <dl v-else>
                  <dt>Run</dt>
                  <dd class="run-id">{{selected.run_id}}</dd>
                  <dt>Account</dt>
                  <dd>{{selected.account_name}} <span class="muted">({{selected.account_id}})</span></dd>
                  <dt>Scope</dt>
                  <dd>{{selected.backup_type}} &middot; {{selected.action}} &middot; {{selected.region}}</dd>
                  <dt>Finished</dt>
                  <dd>{{formatTimestamp(selected.ended_at)}}</dd>
                  <dt>Status</dt>
                  <dd><span class="status-chip" :class="statusClass(selected.status)">{{selected.status}}</span></dd>
                  <dt>Shelvery</dt>
                  <dd>{{selected.shelvery_version}}</dd>
                </dl>

                <div class="detail-section" v-if="isMonthView">
                  <h5>Day by day</h5>

                  <table class="entries">
                    <thead>
                      <tr>
                        <th>Day</th>
                        <th>Status</th>
                        <th>Runs</th>
                        <th>Runs failed</th>
                        <th>Resources failed</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="day in selectedDays" :key="day.day">
                        <td>{{formatDay(day.day)}}</td>
                        <td><span class="status-chip" :class="statusClass(day.status)">{{day.status}}</span></td>
                        <td>{{day.runs}}</td>
                        <td>{{day.failed}}</td>
                        <td>{{day.resources_failed}}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div class="detail-section">
                  <h5>{{isMonthView ? 'Resources, last run of the month' : 'Resources'}}</h5>

                  <p class="kpi-note mb-0" v-if="detailEntries.length === 0">
                    No per resource detail was recorded for this run.
                  </p>

                  <table class="entries" v-else>
                    <thead>
                      <tr>
                        <th>Operation</th>
                        <th>Resource</th>
                        <th>Retention</th>
                        <th>Backup</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <template v-for="(entry, index) in detailEntries">
                        <tr :key="'entry-' + index" :class="{'has-error': entry.error}">
                          <td>{{entry.operation}}</td>
                          <td>
                            <span class="entry-name">{{entry.entity_name || entry.entity_id || '&mdash;'}}</span>
                            <span class="entry-id" v-if="entry.entity_name && entry.entity_id">{{entry.entity_id}}</span>
                          </td>
                          <td>{{entry.retention_type || '&mdash;'}}</td>
                          <td class="entry-backup">{{entry.backup_name || '&mdash;'}}</td>
                          <td><span class="status-chip" :class="statusClass(entry.status)">{{entry.status}}</span></td>
                        </tr>
                        <tr class="entry-error-row" v-if="entry.error" :key="'err-' + index">
                          <td colspan="5">{{entry.error}}</td>
                        </tr>
                      </template>
                    </tbody>
                  </table>

                  <p class="kpi-note" v-if="detailOmitted > 0">
                    {{detailOmitted}} further successful entries were omitted from this report.
                  </p>
                </div>
              </div>
            </div>
          </transition>

        </template>
      </div>
    </div>

    <div class="row empty-state" v-if="allReports.length === 0">
      <div class="col text-center">
        <h4 v-if="period">No shelvery runs reported for {{periodLabel}}</h4>
        <h4 v-else>No Shelvery run status available</h4>
        <p v-if="period">History starts from the day shelvery began publishing status reports. Pick another period, or go back to Latest.</p>
        <p v-else>Run status will appear here once shelvery is publishing status reports for this contract.</p>
      </div>
    </div>
  </div>
</template>

<script>
import HTTP from '@/common/http'
import VueLoading from 'vue-loading-overlay'
import { mapGetters } from 'vuex'

// A report is only replaced when that same account/region/type/action runs again, so a run
// that stops happening leaves its last result behind forever. Anything older than a day and
// a bit hasn't reported on schedule, and is counted as stale rather than as current state.
const STALE_AFTER_HOURS = 26

// Actions with a tab of their own. Everything else (create_data_buckets and the do_* legs
// shelvery fans out to its own invocations) falls through to the Internal tab.
const TABBED_ACTIONS = ['create_backups', 'clean_backups', 'pull_shared_backups']

// how far back the period dropdown offers. Nothing enumerates which periods hold data, so
// the options are generated and an empty one simply 404s - the same bargain every other
// date driven view in this app makes.
const DAY_OPTIONS = 14
const MONTH_OPTIONS = 12

export default {
  name: 'ShelveryStatus',
  components: {
    VueLoading
  },
  props: [
    'id',
    'type'
  ],
  computed: {
    ...mapGetters([
      'activeType'
    ]),
    accounts () {
      const names = this.allReports.map(report => report.account_name || report.account_id)
      return names.filter((name, index) => names.indexOf(name) === index).sort()
    },
    isMonthView () {
      return this.period === 'month'
    },
    dayOptions () {
      return [...Array(DAY_OPTIONS)].map((value, index) =>
        this.$moment().subtract(index, 'days').format('YYYY-MM-DD'))
    },
    monthOptions () {
      return [...Array(MONTH_OPTIONS)].map((value, index) =>
        this.$moment().subtract(index, 'months').format('YYYY-MM'))
    },
    periodLabel () {
      if (this.period === 'day') return this.formatDay(this.date)
      if (this.period === 'month') return this.formatMonth(this.date)
      return 'Latest'
    },
    periodShort () {
      // the navbar already carries the tabs, the account and the CSV button
      if (this.period === 'day') return this.$moment(this.date, 'YYYY-MM-DD').format('D MMM')
      if (this.period === 'month') return this.$moment(this.date, 'YYYY-MM').format('MMM YYYY')
      return 'Latest'
    },
    healthHeading () {
      return this.period ? 'Health \u2014 ' + this.periodLabel : 'Health'
    },
    kpiNote () {
      // inactive runs are counted as passed - they are hidden from the table by default,
      // so the composition of the headline number has to be stated somewhere
      const inactive = 'runs that collected nothing are counted as passed and hidden from the ' +
        'table by default'
      if (this.isMonthView) {
        return 'counts cover every account and action, one row per thing shelvery reports on, ' +
          'summed over the days it ran that month; ' + inactive
      }
      if (this.period) {
        return 'counts cover every account and action for the runs that reported on this day; ' +
          inactive
      }
      return 'counts cover every account and action, for runs that reported in the last ' +
        this.staleHours + ' hours; older runs are shown greyed in the table; ' + inactive
    },
    tableColumns () {
      return this.isMonthView ? this.activeTab.monthColumns : this.activeTab.columns
    },
    selectedDays () {
      // the rollup keys days by date; the table wants them in order, newest first
      const days = (this.selected && this.selected.days) || {}
      return Object.keys(days).sort().reverse().map(day => Object.assign({day: day}, days[day]))
    },
    detailEntries () {
      if (!this.selected) return []
      // a rollup has no entries of its own, but carries the last full report of the month
      const source = this.isMonthView ? this.selected.latest : this.selected
      return (source && source.entries) || []
    },
    detailOmitted () {
      if (!this.selected) return 0
      const source = this.isMonthView ? this.selected.latest : this.selected
      return (source && source.entries_omitted) || 0
    },
    visibleTabs () {
      // built from every report, not just the selected account, so the tab set doesn't
      // shift under you when you switch accounts
      return this.tabs.filter(tab => this.allReports.some(report => this.matchesTab(report, tab)))
    },
    activeTab () {
      return this.tab || this.visibleTabs[0] || this.tabs[0]
    },
    tabReports () {
      return this.allReports.filter(report =>
        (report.account_name || report.account_id) === this.activeAccount &&
        this.matchesTab(report, this.activeTab))
    },
    hiddenInactive () {
      return this.tabReports.filter(report => this.isInactive(report)).length
    },
    rows () {
      let rows = this.tabReports
      if (!this.showInactive) {
        rows = rows.filter(report => !this.isInactive(report))
      }
      if (this.showOnlyFailures) {
        rows = rows.filter(report => report.status !== 'OK')
      }
      return rows
    },
    // the counters below deliberately span every account and action, so a failure never
    // hides behind the tab or account you happen to have selected
    freshReports () {
      return this.allReports.filter(report => !this.isStale(report))
    },
    staleCount () {
      return this.allReports.length - this.freshReports.length
    },
    counts () {
      const counts = { OK: 0, PARTIAL: 0, FAILED: 0 }
      this.freshReports.forEach((report) => {
        if (counts[report.status] !== undefined) counts[report.status] += 1
      })
      return counts
    },
    failedResources () {
      // a rollup already totals the month; a run carries its own summary
      return this.freshReports.reduce((total, report) => total + (this.isMonthView
        ? (report.resources_failed || 0)
        : ((report.summary && report.summary.failed) || 0)), 0)
    },
    failingRuns () {
      return this.freshReports.filter(report => report.status !== 'OK')
    },
    inactiveTotal () {
      // spans every account and action, unlike hiddenInactive which follows the current tab
      return this.freshReports.filter(report => this.isInactive(report)).length
    },
    kpis () {
      // tone is only applied when the number means something - a zero failure count is
      // muted, not red, so colour never cries wolf
      const resources = { label: 'resources not backed up', value: this.failedResources, tone: this.failedResources ? 'bad' : 'muted' }
      // a subset of the passed count, not an addition to it - labelled so it cannot be
      // read as a fourth outcome. Never toned: doing nothing is neither good nor bad.
      const inactive = { label: 'of which inactive', value: this.inactiveTotal, tone: 'muted' }

      if (this.isMonthView) {
        // a row is one thing shelvery reports on, counted over the whole month
        return [
          { label: 'clean all month', value: this.counts.OK, tone: this.counts.OK ? 'good' : 'muted' },
          { label: 'had a partial day', value: this.counts.PARTIAL, tone: this.counts.PARTIAL ? 'warn' : 'muted' },
          { label: 'had a failed day', value: this.counts.FAILED, tone: this.counts.FAILED ? 'bad' : 'muted' },
          inactive,
          resources
        ]
      }

      const tiles = [
        { label: 'runs passed', value: this.counts.OK, tone: this.counts.OK ? 'good' : 'muted' },
        { label: 'runs partially failed', value: this.counts.PARTIAL, tone: this.counts.PARTIAL ? 'warn' : 'muted' },
        { label: 'runs failed', value: this.counts.FAILED, tone: this.counts.FAILED ? 'bad' : 'muted' },
        inactive,
        resources
      ]

      // staleness is measured against now, so it means nothing when reading history
      if (!this.period) {
        tiles.push({ label: 'runs not seen in ' + this.staleHours + 'h', value: this.staleCount, tone: this.staleCount ? 'warn' : 'muted' })
      }

      return tiles
    }
  },
  data () {
    // fresh objects per tab rather than shared references - vue-good-table keeps sort state
    // on the column objects it is handed
    const dimensions = () => ([
      { label: 'Region', field: 'region', filterOptions: { enabled: true, placeholder: 'All' } },
      { label: 'Type', field: 'backup_type', filterOptions: { enabled: true, placeholder: 'All' } }
    ])
    const status = () => ({ label: 'Status', field: 'status', filterOptions: { enabled: true, placeholder: 'All' } })
    const finished = () => ({ label: 'Finished', field: 'ended_at', formatFn: this.formatTimestamp })
    const num = (label, field) => ({ label: label, field: 'summary.' + field, type: 'number' })

    // a monthly rollup counts days rather than resources, so the numbers mean the same
    // thing whatever the action - only the Action column varies by tab
    const monthColumns = (extra) => dimensions().concat(extra || []).concat([
      status(),
      { label: 'Days run', field: 'days_run', type: 'number' },
      { label: 'Passed', field: 'days_passed', type: 'number' },
      { label: 'Partial', field: 'days_partial', type: 'number' },
      { label: 'Failed', field: 'days_failed', type: 'number' },
      { label: 'Resources failed', field: 'resources_failed', type: 'number' },
      { label: 'Last run', field: 'last_run', formatFn: this.formatDay }
    ])

    return {
      staleHours: STALE_AFTER_HOURS,
      period: null,
      date: null,
      allReports: [],
      activeAccount: null,
      tab: null,
      selected: null,
      selectedRow: null,
      showOnlyFailures: false,
      showInactive: false,
      loading: false,
      // one column set per action, because the numbers mean different things per action -
      // 'succeeded' is backups created for one and backups deleted for the next
      tabs: [
        {
          id: 'backups',
          name: 'Backups',
          actions: ['create_backups'],
          columns: dimensions().concat([
            status(),
            num('Resources', 'collected'),
            num('Backed up', 'succeeded'),
            num('Skipped', 'skipped'),
            num('Failed', 'failed'),
            finished()
          ]),
          monthColumns: monthColumns()
        },
        {
          id: 'cleanup',
          name: 'Cleanup',
          actions: ['clean_backups'],
          columns: dimensions().concat([
            status(),
            num('Examined', 'collected'),
            num('Deleted', 'succeeded'),
            num('Failed', 'failed'),
            finished()
          ]),
          monthColumns: monthColumns()
        },
        {
          id: 'pull',
          name: 'Pull shared',
          actions: ['pull_shared_backups'],
          columns: dimensions().concat([
            status(),
            num('Found', 'collected'),
            num('Pulled', 'succeeded'),
            num('Failed', 'failed'),
            finished()
          ]),
          monthColumns: monthColumns()
        },
        {
          id: 'internal',
          name: 'Internal',
          actions: null,
          columns: dimensions().concat([
            { label: 'Action', field: 'action', filterOptions: { enabled: true, placeholder: 'All' } },
            status(),
            num('Succeeded', 'succeeded'),
            num('Failed', 'failed'),
            finished()
          ]),
          monthColumns: monthColumns([{ label: 'Action', field: 'action', filterOptions: { enabled: true, placeholder: 'All' } }])
        }
      ]
    }
  },
  watch: {
    id () {
      this.getStatus()
    }
  },
  created () {
    this.getStatus()
  },
  methods: {
    downloadCSV () {
      this.flashMessage.info({
        message: 'Generating file...',
        time: 3000
      })
      HTTP.get(`/shelvery/status/${this.activeType}/${this.id}${this.periodPath()}/csv`)
        .then((response) => {
          window.location = response.data.signedUrl
          this.flashMessage.success({
            message: 'Successfully generated Shelvery run status CSV report'
          })
        })
        .catch((error) => {
          this.flashMessage.error({
            message: `Failed to generate Shelvery run status CSV report : ${error.message}`
          })
        })
    },
    getStatus () {
      this.loading = true
      this.allReports = []
      this.clearSelection()
      HTTP.get(`/shelvery/status/${this.activeType}/${this.id}${this.periodPath()}`)
        .then((response) => {
          this.allReports = response.data.reports
          this.activeAccount = this.defaultAccount()
          this.tab = null
        })
        .catch((error) => {
          if (error.response && error.response.status === 404) return
          this.flashMessage.error({
            message: `Failed to get Shelvery run status: ${error.message}`
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    defaultAccount () {
      // prod first if there is one, otherwise the first alphabetically
      const prod = this.accounts.find(account => account === 'prod')
      return prod || this.accounts[0] || null
    },
    matchesTab (report, tab) {
      if (tab.actions) return tab.actions.indexOf(report.action) !== -1
      return TABBED_ACTIONS.indexOf(report.action) === -1
    },
    isInactive (report) {
      // nothing was examined and nothing happened - the type simply isn't in use here.
      // Guarded on OK so a failure is never hidden.
      // a rollup carries no summary of its own, but does carry the last run of the month
      const summary = report.summary || (report.latest && report.latest.summary) || {}
      return report.status === 'OK' &&
        !summary.collected && !summary.succeeded && !summary.skipped && !summary.failed
    },
    isStale (report) {
      // measured against now, so it only means anything for the latest view
      if (this.period || !report.ended_at) return false
      return this.$moment().diff(this.$moment(report.ended_at), 'hours') >= STALE_AFTER_HOURS
    },
    clearSelection () {
      this.selected = null
      this.selectedRow = null
    },
    setTab (tab) {
      this.tab = tab
      this.clearSelection()
    },
    setAccount (account) {
      this.activeAccount = account
      this.clearSelection()
    },
    periodPath () {
      return this.period ? `/${this.period}/${this.date}` : ''
    },
    setPeriod (period, date) {
      if (this.period === period && this.date === date) return
      this.period = period
      this.date = date
      // fetched directly rather than through a watch, because period and date change
      // together and a watch on each would fire two requests
      this.getStatus()
    },
    runKey (run) {
      // a rollup has no run_id, but every report is identified by what it ran for
      return [run.account_id, run.region, run.backup_type, run.action].join('-')
    },
    jumpTo (run) {
      this.activeAccount = run.account_name || run.account_id
      this.tab = this.tabs.find(tab => this.matchesTab(run, tab))
      this.selected = run
      this.selectedRow = null
    },
    onRowClick (params) {
      this.selected = params.row
      this.selectedRow = params.row.originalIndex
    },
    rowStyleFn (row) {
      if (row.originalIndex === this.selectedRow) return 'selected-row'
      if (this.isStale(row)) return 'stale-row'
      if (row.status === 'FAILED') return 'highlight-row-red'
      if (row.status === 'PARTIAL') return 'highlight-row-amber'
      return ''
    },
    statusClass (status) {
      if (status === 'OK') return 'status-ok'
      if (status === 'PARTIAL') return 'status-partial'
      if (status === 'IGNORE') return 'status-skipped'
      return 'status-failed'
    },
    formatTimestamp (value) {
      if (!value) return ''
      return this.$moment(value).format('MMMM Do YYYY, h:mm a')
    },
    formatDay (value) {
      if (!value) return ''
      return this.$moment(value, 'YYYY-MM-DD').format('ddd D MMM YYYY')
    },
    formatMonth (value) {
      if (!value) return ''
      return this.$moment(value, 'YYYY-MM').format('MMMM YYYY')
    }
  }
}
</script>

<style scoped>
/* The row/column pair every other data view uses. Without it the column falls back to
   flex: 0 1 auto and sizes to its content, so the cards visibly resized each time you
   switched tabs. flex: 1 pins them to the full width; overflow: hidden is what lets a
   wide table shrink rather than push the column out (flex items default to min-width:auto). */
.row {
  display: flex;
  padding-left: 20px;
  padding-right: 20px;
}

.column {
  padding-top: 15px;
  flex-direction: column;
  display: flex;
  overflow: hidden;
  flex: 1;
}

/* card chrome matched to PatchManager / Inspector - the app's modern data-view card */
.card {
  transition: opacity 200ms;
  background: #ffffff;
  width: auto;
  padding: 15px;
  border: none;
  border-radius: 0;
  box-sizing: content-box;
  box-shadow: 0 2px 13px -5px rgba(0, 0, 0, 0.2);
  margin-bottom: 10px;
}

.card-heading h4 {
  padding-bottom: 5px;
  font-size: 1.1rem;
  margin: 0;
}

.issue-card {
  border-right: 6px solid #bd3e32;
}

h5 {
  font-size: 0.95rem;
  font-weight: 600;
  margin-top: 8px;
}

.muted {
  color: #aaaaaa;
}

/* KPI row - stat tiles. Values keep the font's proportional figures; tabular-nums is
   for columns that must align vertically, not for display-size numbers. */
.kpi-row {
  display: flex;
  flex-wrap: wrap;
}

.kpi {
  flex: 1 1 0;
  min-width: 130px;
  padding-right: 16px;
}

.kpi-value {
  display: block;
  font-size: 1.9rem;
  font-weight: 600;
  line-height: 1.1;
  color: #2c3e50;
}

.kpi-value.good {
  color: #2e7d32;
}

.kpi-value.warn {
  color: #e65100;
}

.kpi-value.bad {
  color: #d32f2f;
}

.kpi-value.muted {
  color: #aaaaaa;
}

.kpi-label {
  display: block;
  font-size: 0.78rem;
  color: #6c757d;
  margin-top: 2px;
}

.kpi-note {
  font-size: 0.75rem;
  color: #6c757d;
  margin: 12px 0 0 0;
}

/* status chips - the app's .severity-badge spec, with its existing status hexes.
   The chip always carries its label, so colour never encodes state on its own. */
.status-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.status-ok {
  color: #fff;
  background-color: #2e7d32;
}

.status-partial {
  color: #fff;
  background-color: #e65100;
}

.status-failed {
  color: #fff;
  background-color: #d32f2f;
}

.status-skipped {
  color: #333;
  background-color: #e0e0e0;
}

/* needs attention */
.attention-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.attention-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
}

.attention-row:last-child {
  border-bottom: none;
}

.attention-row:hover {
  background-color: #f4f5f8;
}

.attention-account {
  display: block;
  font-weight: 600;
  font-size: 0.9rem;
}

.attention-detail {
  display: block;
  font-size: 0.78rem;
  color: #6c757d;
}

/* runs table - match the type scale the other large tables use, and align figures */
/deep/ .vgt-left-align,
/deep/ .vgt-right-align {
  font-size: 13px;
}

/deep/ .vgt-right-align {
  font-variant-numeric: tabular-nums;
}

/* run detail - the house key/value grid used by Inspector and PatchManager */
.run-detail dl {
  display: grid;
  grid-template-columns: max-content auto;
  margin: 0;
  gap: 2px 10px;
}

.run-detail dt {
  grid-column-start: 1;
  font-weight: 600;
  margin-bottom: 2px;
}

.run-detail dd {
  grid-column-start: 2;
  margin-bottom: 0;
}

.run-id {
  font-size: 0.85rem;
}

.detail-section {
  margin-top: 10px;
  padding-top: 6px;
  border-top: 1px solid #eee;
}

/* per resource entries - the header row is what names each field */
.entries {
  width: 100%;
  font-size: 0.82rem;
  border-collapse: collapse;
}

.entries th {
  text-align: left;
  font-weight: 600;
  color: #6c757d;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #dcdfe6;
  padding: 4px 10px 4px 0;
}

.entries td {
  padding: 5px 10px 5px 0;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: top;
}

/* an entry and its error read as one unit, so drop the divider between them */
.entries tr.has-error td {
  border-bottom: none;
  padding-bottom: 1px;
}

.entry-name {
  display: block;
  font-weight: 500;
  color: #2c3e50;
}

.entry-id {
  display: block;
  color: #aaaaaa;
  font-size: 0.75rem;
}

.entry-backup {
  color: #6c757d;
  word-break: break-all;
}

.entry-error-row td {
  color: #d32f2f;
  font-size: 0.75rem;
  padding: 0 10px 6px 0;
  border-bottom: 1px solid #f0f0f0;
}

/* sub navbar */
.fixed-top-sub {
  position: sticky;
  top: 56px;
  z-index: 1000;
  margin-bottom: 20px;
}

.scrollable-menu {
  height: auto;
  max-height: 80vh;
  overflow-x: scroll;
}

/* the selected period, so a long dropdown still shows where you are */
.dropdown-item.active-period {
  font-weight: 600;
  color: #2c3e50;
  background-color: #f4f5f8;
}

ul.nav a {
  color: rgba(0, 0, 0, 0.5);
}

ul.nav a:hover {
  cursor: pointer;
  color: rgba(0, 0, 0, 1);
}

.active {
  color: rgba(0, 0, 0, 0.8) !important;
}

.btn-margin {
  margin-right: 5px;
}

.empty-state {
  margin-top: 50px;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 200ms ease-in-out;
}

.fade-enter-to {
  opacity: 1;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>



// WEBPACK FOOTER //
// src/views/ShelveryStatus.vue